import { useLocation } from 'react-router-dom'

import { type ClassValue, clsx } from 'clsx'

import { twMerge } from 'tailwind-merge'

import { differenceInMilliseconds } from '@/lib/date'

import toast from '../components/toast'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function membershipProgressPctg(startDate?: string | Date | null, endDate?: string | Date | null): number {
  if (!startDate || !endDate) return 0

  const now = new Date()
  const start = new Date(startDate)
  const end = new Date(endDate)

  if (now <= start) return 0
  if (now >= end) return 100

  const total = differenceInMilliseconds(end, start)
  const current = differenceInMilliseconds(now, start)

  return Math.round((current / total) * 100)
}

/**
 * URLSearchParams methods.
 */
export function useUrlQuery() {
  const search = useLocation().search
  const query = new URLSearchParams(search)
  return {
    params: Object.fromEntries(query.entries()), // Record<string, string>
    query, // Original URLSearchParams
  }
}

export function toTitleCase(str: string): string {
  if (typeof str !== 'string') return ''

  return str
    .toLowerCase()
    .split(/\s+/) // handle multiple spaces, tabs, etc.
    .map((word) => (word.length > 0 ? word.charAt(0).toUpperCase() + word.slice(1) : ''))
    .join(' ')
}

/**
 * Capitalizes each word in a full name
 * Handles names with multiple spaces, hyphens, and apostrophes correctly
 * @param name - The full name to capitalize
 * @returns Properly capitalized name
 * @example
 * capitalizeName('john doe') => 'John Doe'
 * capitalizeName('mary-jane smith') => 'Mary-Jane Smith'
 * capitalizeName("o'connor") => "O'Connor"
 */
export function capitalizeName(name: string | null | undefined): string {
  if (!name || typeof name !== 'string') return ''

  return name
    .toLowerCase()
    .split(/\s+/)
    .map((word) => {
      // Handle hyphenated names (e.g., Mary-Jane)
      if (word.includes('-')) {
        return word
          .split('-')
          .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
          .join('-')
      }
      // Handle names with apostrophes (e.g., O'Connor)
      if (word.includes("'")) {
        return word
          .split("'")
          .map((part, _idx) => {
            // Capitalize after apostrophe
            return part.charAt(0).toUpperCase() + part.slice(1)
          })
          .join("'")
      }
      // Regular word
      return word.charAt(0).toUpperCase() + word.slice(1)
    })
    .join(' ')
}
interface LongTextProps {
  children: string
  className?: string
  lines?: number // defaults to 1-line truncation
}

export function LongText({ children, className, lines = 1 }: LongTextProps) {
  const lineClampClass = lines === 1 ? 'truncate' : `line-clamp-${lines} overflow-hidden`

  return <span className={cn('block', lineClampClass, className)}>{children}</span>
}

export function calculateEndDate(plan?: {
  duration_value?: number | null
  duration_unit?: 'day' | 'week' | 'month' | 'year' | null
}) {
  const startDate = new Date()

  if (plan?.duration_value && plan?.duration_unit) {
    const endDate = new Date(startDate)

    switch (plan.duration_unit) {
      case 'day':
        endDate.setDate(endDate.getDate() + plan.duration_value)
        break
      case 'week':
        endDate.setDate(endDate.getDate() + plan.duration_value * 7)
        break
      case 'month':
        endDate.setMonth(endDate.getMonth() + plan.duration_value)
        break
      case 'year':
        endDate.setFullYear(endDate.getFullYear() + plan.duration_value)
        break
    }

    return { startDate, endDate }
  }

  // fallback if no duration
  return {
    startDate,
    endDate: new Date(startDate.getTime() + 30 * 86400000),
  }
}

export function deepMerge(obj1: any, obj2: any): any {
  const result = { ...obj1 }
  for (const key in obj2) {
    if (obj2[key] && typeof obj2[key] === 'object' && !Array.isArray(obj2[key])) {
      result[key] = deepMerge(obj1[key] ?? {}, obj2[key])
    } else {
      result[key] = obj2[key]
    }
  }
  return result
}

export function formatMinutes(minutes: number): string {
  if (minutes < 0) return '0 minutes'

  const days = Math.floor(minutes / (24 * 60))
  const hours = Math.floor((minutes % (24 * 60)) / 60)
  const mins = minutes % 60

  const parts: string[] = []

  if (days > 0) parts.push(`${days} ${days === 1 ? 'day' : 'days'}`)
  if (hours > 0) parts.push(`${hours} ${hours === 1 ? 'hr' : 'hrs'}`)
  if (mins > 0 || parts.length === 0) parts.push(`${mins} ${mins === 1 ? 'min' : 'mins'}`)

  return parts.join(' ')
}
export function copyText(text: string, msg: string) {
  navigator.clipboard.writeText(text)
  toast.success(msg)
}

export function isValidURL(url: string): boolean {
  try {
    new URL(url)
    return true
  } catch {
    return false
  }
}

/**
 * Detect if the app is running in an in-app browser (Instagram, Facebook, etc.)
 */
export function isInAppBrowser(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false

  const ua = navigator.userAgent || ''

  // Check for common in-app browser identifiers
  return (
    ua.includes('Instagram') ||
    ua.includes('FBAN') || // Facebook App
    ua.includes('FBAV') || // Facebook App
    ua.includes('FB_IAB') || // Facebook In-App Browser
    ua.includes('Twitter') ||
    ua.includes('Line/') ||
    ua.includes('snapchat') ||
    ua.includes('Pinterest') ||
    ua.includes('LinkedIn') ||
    ua.includes('TikTok') ||
    ua.includes('Messenger')
  )
}

/**
 * Get the name of the in-app browser
 */
export function getInAppBrowserName(): string {
  if (typeof navigator === 'undefined') return 'Unknown'

  const ua = navigator.userAgent || ''

  if (ua.includes('Instagram')) return 'Instagram'
  if (ua.includes('FBAN') || ua.includes('FBAV') || ua.includes('FB_IAB')) return 'Facebook'
  if (ua.includes('Twitter')) return 'Twitter'
  if (ua.includes('Line/')) return 'Line'
  if (ua.includes('snapchat')) return 'Snapchat'
  if (ua.includes('Pinterest')) return 'Pinterest'
  if (ua.includes('LinkedIn')) return 'LinkedIn'
  if (ua.includes('TikTok')) return 'TikTok'
  if (ua.includes('Messenger')) return 'Messenger'

  return 'In-App Browser'
}
